import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HelpCircle, MapPin } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ObrigatoriedadeConfig } from '@/hooks/useObrigatoriedadesItem';

interface EnderecoData {
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
}

interface Props {
  config: ObrigatoriedadeConfig;
  endereco: EnderecoData;
  onChange: (endereco: EnderecoData) => void;
  onBlur?: () => void;
  erro?: string;
  obrigatorio?: boolean;
}

export function EnderecoField({ config, endereco, onChange, onBlur, erro, obrigatorio = false }: Props) {
  const buscarCEP = async (cep: string) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
        const data = await response.json();
        if (!data.erro) {
          onChange({
            ...endereco,
            cep: cepLimpo,
            logradouro: data.logradouro,
            bairro: data.bairro,
            cidade: data.localidade,
            uf: data.uf,
          });
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  const isValid = !erro && !!(
    endereco.cep && 
    endereco.logradouro && 
    endereco.numero && 
    endereco.bairro && 
    endereco.cidade && 
    endereco.uf
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-muted-foreground" />
        <Label className="font-medium">
          {config.obrigatoriedades_catalogo.nome} {obrigatorio && '*'}
        </Label>
        {config.mensagem_ajuda && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger type="button">
                <HelpCircle className="h-3 w-3 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">{config.mensagem_ajuda}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {isValid && obrigatorio && (
          <span className="text-xs text-success">✓</span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">CEP {obrigatorio && '*'}</Label>
          <Input
            placeholder="00000-000"
            value={endereco.cep || ''}
            onChange={(e) => {
              const valor = e.target.value.replace(/\D/g, '');
              onChange({ ...endereco, cep: valor });
            }}
            onBlur={(e) => {
              buscarCEP(e.target.value);
              onBlur?.();
            }}
            className={erro ? 'border-destructive' : ''}
            maxLength={8}
          />
        </div>

        <div>
          <Label className="text-xs">UF {obrigatorio && '*'}</Label>
          <Input
            placeholder="RS"
            value={endereco.uf || ''}
            onChange={(e) =>
              onChange({ ...endereco, uf: e.target.value.toUpperCase() })
            }
            onBlur={onBlur}
            className={erro ? 'border-destructive' : ''}
            maxLength={2}
          />
        </div>
      </div>

      <div>
        <Label className="text-xs">Logradouro {obrigatorio && '*'}</Label>
        <Input
          placeholder="Rua, Avenida, etc"
          value={endereco.logradouro || ''}
          onChange={(e) => onChange({ ...endereco, logradouro: e.target.value })}
          onBlur={onBlur}
          className={erro ? 'border-destructive' : ''}
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Número {obrigatorio && '*'}</Label>
          <Input
            placeholder="123"
            value={endereco.numero || ''}
            onChange={(e) => onChange({ ...endereco, numero: e.target.value })}
            onBlur={onBlur}
            className={erro ? 'border-destructive' : ''}
          />
        </div>

        <div className="col-span-2">
          <Label className="text-xs">Complemento</Label>
          <Input
            placeholder="Apto, Sala, Bloco..."
            value={endereco.complemento || ''}
            onChange={(e) => onChange({ ...endereco, complemento: e.target.value })}
            onBlur={onBlur}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Bairro {obrigatorio && '*'}</Label>
          <Input
            placeholder="Nome do bairro"
            value={endereco.bairro || ''}
            onChange={(e) => onChange({ ...endereco, bairro: e.target.value })}
            onBlur={onBlur}
            className={erro ? 'border-destructive' : ''}
          />
        </div>

        <div>
          <Label className="text-xs">Cidade {obrigatorio && '*'}</Label>
          <Input
            placeholder="Nome da cidade"
            value={endereco.cidade || ''}
            onChange={(e) => onChange({ ...endereco, cidade: e.target.value })}
            onBlur={onBlur}
            className={erro ? 'border-destructive' : ''}
          />
        </div>
      </div>

      {!obrigatorio && !isValid && (
        <p className="text-xs text-muted-foreground">
           Opcional agora - será obrigatório ao fechar o pedido
        </p>
      )}

      {erro && obrigatorio && (
        <p className="text-xs text-destructive">
          {config.mensagem_erro || erro}
        </p>
      )}
    </div>
  );
}
